'use strict';

const libPictProvider = require('pict-provider');

/**
 * The catalog of downloadable desktop apps we ship to customers, each gated by a Module (ModuleHash). This is the
 * first of three planned binary apps — add the next two as entries here (drop their installers in ./downloads/,
 * add a copyFiles rule, and a Module catalog row + a nav entry gated on the new ModuleHash).
 *
 * Files live in the committed ./downloads/ folder (copied to serve/…/downloads/ at build) and are linked as
 * plain static URLs. NOTE: gating here is PRESENTATIONAL — the static file URLs are public on the deployed
 * static webserver, so a customer without the module simply doesn't see the links; the bytes are not
 * access-controlled. (Byte-level enforcement would require serving through the authenticated /1.0/ API or a
 * session-checked download endpoint — a deliberate future step; UI-level gating was the chosen scope.)
 */
// One module definition per downloadable desktop app — each app is its OWN DOWNLOAD_<APP> module + constant
// (the ModuleHash carries the same DOWNLOAD_ prefix so downloadable-app modules are distinct from the platform
// service modules like HEADLIGHT_FIELDBOOK). The other two binary apps each get their own constant, added to
// DOWNLOAD_MODULES below.
// IMPORTANT: the CustomerModuleJoin.ModuleHash column is char(24) — a longer hash is SILENTLY TRUNCATED on write
// (which breaks the enable/gate match). Keep every ModuleHash here to 24 characters or fewer.
const DOWNLOAD_DATA_DOWNLOADER =
{
	Key: 'data-downloader',
	ModuleHash: 'DOWNLOAD_DATA_DOWNLOADER',
	Name: 'Headlight Data Downloader',
	Version: '0.9.3',
	Icon: 'fas fa-cloud-arrow-down',
	Blurb: 'A desktop app for bulk-downloading a project’s observations, documents, and media to your computer.',
	Builds:
	[
		{ OS: 'macOS', Note: 'Apple Silicon', OSIcon: 'fab fa-apple', File: 'Headlight_Downloader_0.9.3_aarch64.dmg', Kind: 'Disk image (.dmg)' },
		{ OS: 'Windows', Note: '64-bit', OSIcon: 'fab fa-windows', File: 'Headlight_Downloader_0.9.3_x64-setup.exe', Kind: 'Installer (.exe)' },
		{ OS: 'Windows', Note: '64-bit', OSIcon: 'fab fa-windows', File: 'Headlight_Downloader_0.9.3_x64_en-US.msi', Kind: 'MSI package (.msi)' },
		{ OS: 'Linux', Note: 'Debian / Ubuntu', OSIcon: 'fab fa-linux', File: 'Headlight_Downloader_0.9.3_amd64.deb', Kind: 'Package (.deb)' },
	],
};

// The registry the view + the ManageCustomers Modules tab iterate. Add each new app's DOWNLOAD_<APP> constant here.
const DOWNLOAD_MODULES = [ DOWNLOAD_DATA_DOWNLOADER ];

const BODY_MOUNT = '#DD-Body';

/**
 * DataDownloaderProvider — data + render for the #/Datalake/DataDownloader screen. Lists each downloadable app
 * the CURRENT customer has enabled (a CustomerModuleJoin for the session's customer, active date window). The nav
 * entry is already gated on the same module, so a customer without it never sees the link; this view re-checks so
 * a stale deep-link shows a clean "not enabled" notice instead of dead download buttons.
 */
class DataDownloaderProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get app() { return this.pict.PictApplication; }

	get state()
	{
		if (!this.pict.AppData.DataDownloader) { this.pict.AppData.DataDownloader = { Ready: false }; }
		return this.pict.AppData.DataDownloader;
	}

	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/** Refresh the current customer's enabled modules (customer-scoped server-side) then paint. */
	async loadAsync()
	{
		if (this.app && (typeof this.app.loadCustomerModulesAsync === 'function'))
		{
			try { await this.app.loadCustomerModulesAsync(); } catch (pError) { /* fail-closed: render shows the gated notice */ }
		}
		this.state.Ready = true;
		this.render();
	}

	_hasModule(pHash)
	{
		return !!(this.app && (typeof this.app._customerHasModule === 'function') && this.app._customerHasModule(pHash));
	}

	render()
	{
		if (this.pict.ContentAssignment.getElement(BODY_MOUNT).length === 0) { return; }
		this.pict.ContentAssignment.assignContent(BODY_MOUNT, this._bodyHTML());
	}

	_bodyHTML()
	{
		const tmpEnabledApps = DOWNLOAD_MODULES.filter((pApp) => this._hasModule(pApp.ModuleHash));
		const tmpHead = `<div class="dd-head"><div class="dd-eyebrow">Data Lake</div><h1 class="dd-title">Data Downloader</h1>`
			+ `<p class="dd-intro">Desktop tools for working with your Headlight data offline. Install one on your computer, sign in, and pull your project data down.</p></div>`;
		if (tmpEnabledApps.length === 0)
		{
			return tmpHead + this._gatedHTML();
		}
		return tmpHead + tmpEnabledApps.map((pApp) => this._appHTML(pApp)).join('');
	}

	_gatedHTML()
	{
		return `<div class="dd-gated">`
			+ `<div class="dd-gated-icon"><i class="fas fa-lock"></i></div>`
			+ `<div class="dd-gated-title">Not enabled for your organization</div>`
			+ `<div class="dd-gated-body">The Data Downloader is a licensed add-on. Ask your Headlight administrator to enable it for your organization.</div>`
			+ `</div>`;
	}

	_appHTML(pApp)
	{
		const tmpBuilds = pApp.Builds.map((pBuild) => this._buildHTML(pApp, pBuild)).join('');
		return `<div class="dd-app">`
			+ `<div class="dd-app-head"><span class="dd-app-icon"><i class="${this._esc(pApp.Icon)}"></i></span>`
			+ `<div><div class="dd-app-name">${this._esc(pApp.Name)}</div>`
			+ `<div class="dd-app-meta">Version ${this._esc(pApp.Version)}</div></div></div>`
			+ `<p class="dd-app-blurb">${this._esc(pApp.Blurb)}</p>`
			+ `<div class="dd-builds">${tmpBuilds}</div>`
			+ `</div>`;
	}

	_buildHTML(pApp, pBuild)
	{
		// Relative static path (served from serve/…/downloads/); the download attribute keeps the installer's name.
		const tmpHref = `downloads/${encodeURIComponent(pBuild.File)}`;
		return `<a class="dd-build" href="${this._esc(tmpHref)}" download="${this._esc(pBuild.File)}">`
			+ `<span class="dd-build-os"><i class="${this._esc(pBuild.OSIcon)}"></i></span>`
			+ `<span class="dd-build-text"><span class="dd-build-title">${this._esc(pBuild.OS)}</span>`
			+ `<span class="dd-build-sub">${this._esc(pBuild.Note)} · ${this._esc(pBuild.Kind)}</span></span>`
			+ `<span class="dd-build-dl"><i class="fas fa-download"></i></span>`
			+ `</a>`;
	}
}

module.exports = DataDownloaderProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'DataDownloaderProvider',
	AutoInitialize: true,
	AutoInitializeOrdinal: 227,
};

module.exports.DOWNLOAD_MODULES = DOWNLOAD_MODULES;
module.exports.DOWNLOAD_DATA_DOWNLOADER = DOWNLOAD_DATA_DOWNLOADER;
